/**
 * Vendor registry API service for vendor companies and their members.
 */

import { sdk, sdkCall } from "./sdk"

export type { Vendor, VendorUser } from "@/generated/sdk"

import type { Vendor, VendorUser } from "@/generated/sdk"

export interface CreateVendorRequest {
  name: string
  type?: string
  contact_email?: string
  contact_name?: string
  notes?: string
}

interface UpdateVendorRequest {
  name?: string
  type?: string
  contact_email?: string
  contact_name?: string
  notes?: string
}

class VendorRegistryApiService {
  async listVendorsPage(opts?: {
    limit?: number
    nextToken?: string
  }): Promise<{ vendors: Vendor[]; nextToken?: string }> {
    const resp = await sdkCall(sdk.listVendors({ limit: opts?.limit, nextToken: opts?.nextToken }))
    return {
      vendors: resp.vendors ?? [],
      nextToken: resp.next_token,
    }
  }

  async listVendors(): Promise<Vendor[]> {
    const all: Vendor[] = []
    let nextToken: string | undefined

    do {
      const page = await this.listVendorsPage({ nextToken })
      all.push(...page.vendors)
      nextToken = page.nextToken
    } while (nextToken)

    return all
  }

  async getVendor(vendorId: string): Promise<Vendor> {
    try {
      return await sdkCall(sdk.getVendor({ vendorId }))
    } catch (err) {
      throw new Error(
        `Failed to load vendor: ${err instanceof Error ? err.message : "unknown error"}`,
      )
    }
  }

  async createVendor(request: CreateVendorRequest): Promise<Vendor> {
    try {
      return await sdkCall(sdk.createVendor({ createVendorRequest: request }))
    } catch (err) {
      throw new Error(
        `Failed to create vendor: ${err instanceof Error ? err.message : "unknown error"}`,
      )
    }
  }

  async updateVendor(vendorId: string, request: UpdateVendorRequest): Promise<Vendor> {
    return sdkCall(sdk.updateVendor({ vendorId, updateVendorRequest: request }))
  }

  async listVendorUsersPage(opts: {
    vendorId: string
    limit?: number
    nextToken?: string
  }): Promise<{ users: VendorUser[]; nextToken?: string }> {
    const resp = await sdkCall(
      sdk.listVendorUsers({
        vendorId: opts.vendorId,
        limit: opts.limit,
        nextToken: opts.nextToken,
      }),
    )
    return { users: resp.items, nextToken: resp.nextToken }
  }

  /**
   * List every member of a vendor company, across all pages.
   */
  async listVendorUsers(vendorId: string): Promise<VendorUser[]> {
    const all: VendorUser[] = []
    let nextToken: string | undefined

    do {
      const page = await this.listVendorUsersPage({ vendorId, nextToken })
      all.push(...page.users)
      nextToken = page.nextToken
    } while (nextToken)

    return all
  }

  /**
   * Add the person with `email` to a vendor company, creating their principal if needed. A member
   * of another company moves here.
   */
  async addVendorUser(vendorId: string, email: string): Promise<VendorUser> {
    return sdkCall(sdk.addVendorUser({ vendorId, addVendorUserRequest: { email } }))
  }

  /**
   * Remove a person from a vendor company. Returns 409 if they have since moved to another company,
   * so this never undoes someone else's change.
   */
  async removeVendorUser(vendorId: string, principalId: string): Promise<void> {
    await sdkCall(sdk.removeVendorUser({ vendorId, principalId }))
  }
}

export const vendorRegistryApiService = new VendorRegistryApiService()
